// components/BookForm.tsx
import { AdultsDropdown, CheckIn, CheckOut, KidsDropdown } from './index';
import { useRoomStore } from '../store/roomStore';
import { FaSearch } from 'react-icons/fa';

/**
 * Booking form component for capturing check-in/out dates and guest counts.
 * 
 * Layout:
 * - Mobile: Stacked vertically (flex-col)
 * - Desktop: Single row (flex-row) with equal-width fields
 * 
 * Each input field is separated by a right border (border-r) for visual clarity.
 * 
 * @returns {JSX.Element} The booking form with date pickers, guest dropdowns, and submit button.
 * 
 * @remarks
 * Clicking "Check Now" triggers the room filter via handleCheck() from RoomStore,
 * which filters available rooms based on total guest count and displays a loading spinner.
 */
export default function BookForm() {
  const { handleCheck } = useRoomStore();

  return (
    <form className="w-full max-w-6xl mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-stretch gap-2 lg:gap-0 bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100/50 backdrop-blur-sm">
        {/* Check In */}
        <div className="flex-1 min-w-0 border-b lg:border-b-0 lg:border-r border-gray-200 hover:bg-gray-50/50 transition-colors duration-200">
          <div className="px-4 py-3 lg:py-0">
            <label className="block text-xs font-primary font-semibold text-gray-500 uppercase tracking-wider mb-1 lg:mb-0.5">
              Check In
            </label>
            <CheckIn />
          </div>
        </div>

        {/* Check Out */}
        <div className="flex-1 min-w-0 border-b lg:border-b-0 lg:border-r border-gray-200 hover:bg-gray-50/50 transition-colors duration-200">
          <div className="px-4 py-3 lg:py-0">
            <label className="block text-xs font-primary font-semibold text-gray-500 uppercase tracking-wider mb-1 lg:mb-0.5">
              Check Out
            </label>
            <CheckOut />
          </div>
        </div>

        {/* Adults */}
        <div className="flex-1 min-w-0 border-b lg:border-b-0 lg:border-r border-gray-200 hover:bg-gray-50/50 transition-colors duration-200">
          <div className="px-4 py-3 lg:py-0">
            <label className="block text-xs font-primary font-semibold text-gray-500 uppercase tracking-wider mb-1 lg:mb-0.5">
              Adults
            </label>
            <AdultsDropdown />
          </div>
        </div>

        {/* Kids */}
        <div className="flex-1 min-w-0 border-b lg:border-b-0 lg:border-r border-gray-200 hover:bg-gray-50/50 transition-colors duration-200">
          <div className="px-4 py-3 lg:py-0">
            <label className="block text-xs font-primary font-semibold text-gray-500 uppercase tracking-wider mb-1 lg:mb-0.5">
              Children
            </label>
            <KidsDropdown />
          </div>
        </div>

        {/* Submit Button */}
        <div className="lg:flex-shrink-0 p-2 lg:p-2 bg-gradient-to-r from-blue-600 to-blue-700 lg:bg-transparent">
          <button
            type="submit"
            onClick={(e) => handleCheck(e)}
            className="w-full lg:h-full px-6 py-4 lg:py-0 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-primary font-semibold text-sm uppercase tracking-wider rounded-xl lg:rounded-lg transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-[1.02] lg:hover:scale-105 active:scale-95"
          >
            <FaSearch className="text-base lg:text-lg" />
            <span>Check Now</span>
          </button>
        </div>
      </div>
    </form>
  );
}